import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    this.#reopenAfterLoginReturn();
  }

  /**
   * Reopens the drawer after a shopper is redirected back from Shopify's
   * hosted login (`/customer_identity/login?return_to=...`) - set by the
   * "Log in" links in the account drawer (sections/account.liquid), which
   * append `?open_cart_drawer=1` to their `return_to` specifically so a
   * shopper who opened that drawer from "Log in to earn points" in the
   * cart lands back in their cart afterwards instead of on /account (the
   * login endpoint's own default redirect target). Reads a real URL query
   * param rather than `history.state` (see the check above) because
   * `history.state` doesn't survive the actual cross-page navigation a
   * login redirect performs - only same-document `pushState`/`replaceState`
   * calls persist it.
   */
  #reopenAfterLoginReturn() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('open_cart_drawer') !== '1') return;

    url.searchParams.delete('open_cart_drawer');
    history.replaceState(history.state, '', url.toString());

    this.open();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.#historyAbortController?.abort();
  }

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   *
   * Both CartAddEvent and CartUpdateEvent share the same DOM event name ('cart:update').
   * We must only open the drawer on genuine add-to-cart actions, not on quantity
   * changes or item removals that originate from inside the cart itself.
   *
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    // Cart-items-component dispatches CartUpdateEvent (same event name) for quantity
    // changes and deletions. Do not reopen the drawer for those — only open it for
    // genuine add-to-cart actions from product forms, quick-add, etc.
    // assets/cart-state-sync.js only re-syncs a page restored from history - not an add either.
    const source = event.detail?.data?.source;
    const isCartItemsUpdate = source === 'cart-items-component' || source === 'cart-state-sync';
    if (isCartItemsUpdate) {
      this.#announceCartCount(event.detail.resource?.item_count);
      return;
    }

    // Theme settings > Cart > "Auto open cart drawer" (`auto-open` on this element).
    if (this.hasAttribute('auto-open') && !event.detail.data?.b2b) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
